<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();
useHead({
  titleTemplate: () => t("seo.layouts./session-counter.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./session-counter.description") },
    {
      property: "og:title", content: () => t("seo.layouts./session-counter.title")
    },
    { property: "og:description", content: () => t("seo.layouts./session-counter.description") },
    {
      property: "og:image",
      content: "/images/chatbot.png",
    },
    { property: "og:image:width", content: "2668" },
    { property: "og:image:height", content: "2000" },
    { property: "og:image:type", content: "image/png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: "@smsbatcom" },
  ],
});

const REGISTER_URL = 'https://omni.smsbat.com/auth/login?regView=true';
const DOCS_URL = 'https://developers.smsbat.com/';

const popularQuestions = ref([
  {
    question: t('sessionCounterLanding.faq[0].question'),
    answer: t('sessionCounterLanding.faq[0].answer'),
    isOpen: false
  },
  {
    question: t('sessionCounterLanding.faq[1].question'),
    answer: t('sessionCounterLanding.faq[1].answer'),
    isOpen: false
  },
  {
    question: t('sessionCounterLanding.faq[2].question'),
    answer: t('sessionCounterLanding.faq[2].answer'),
    isOpen: false
  },
  {
    question: t('sessionCounterLanding.faq[3].question'),
    answer: t('sessionCounterLanding.faq[3].answer'),
    isOpen: false
  },
])

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})
</script>

<template>
  <div>
    <!-- Hero Banner -->
    <div class="main-banner">
      <div class="main-banner-content !justify-center gap-8">
        <div class="banner-text">
          <div class="banner-title">
            {{ $t('sessionCounterLanding.hero.title') }}
          </div>
          <div class="banner-description">
            {{ $t('sessionCounterLanding.hero.subtitle') }}
          </div>
          <div class="banner-action">
            <NuxtLink :to="REGISTER_URL" external target="_blank" class="button filled whitespace-nowrap !text-sm">
              <p>{{ $t('sessionCounterLanding.hero.cta') }}</p>
            </NuxtLink>
            <NuxtLink :to="DOCS_URL" external target="_blank" class="button outlined">
              {{ $t('sessionCounterLanding.hero.viewDocs') }}
            </NuxtLink>
          </div>
        </div>
        <div class="banner-image-container">
          <NuxtImg preload sizes="xs:100vw sm:100vw md:50vw lg:600px xl:800px xxl:1000px" src="/images/chatbot.png" :alt="$t('seo.layouts./session-counter.title')" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" class="w-full h-auto object-contain" />
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('sessionCounterLanding.features.title') }}</div>
        </div>
        <div class="section-content">
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">
                <span class="pi pi-sync text-3xl"></span>
              </div>
              <h3>{{ $t('sessionCounterLanding.features.apiReplication.title') }}</h3>
              <p>{{ $t('sessionCounterLanding.features.apiReplication.description') }}</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon injection-icon">
                <span class="pi pi-bolt text-3xl"></span>
              </div>
              <h3>{{ $t('sessionCounterLanding.features.injections.title') }}</h3>
              <p>{{ $t('sessionCounterLanding.features.injections.description') }}</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon counter-icon">
                <span class="pi pi-chart-bar text-3xl"></span>
              </div>
              <h3>{{ $t('sessionCounterLanding.features.sessionCounting.title') }}</h3>
              <p>{{ $t('sessionCounterLanding.features.sessionCounting.description') }}</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon cascade-icon">
                <span class="pi pi-sitemap text-3xl"></span>
              </div>
              <h3>{{ $t('sessionCounterLanding.features.cascadeApi.title') }}</h3>
              <p>{{ $t('sessionCounterLanding.features.cascadeApi.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section service-domain" id="pricing">
      <div class="container">
        <div class="service-price">
          <div class="price-label">{{ $t('global.price') }}</div>
        </div>
        <div class="section-head text-center">
          <div class="section-head-title after:opacity-0">{{ $t('sessionCounterLanding.pricing.title') }}</div>
        </div>
        <div class="section-content">
          <div class="pricing-cards three-cols">
            <!-- Connection Fee -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>{{ $t('sessionCounterLanding.pricing.connection.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">39 EUR</span>
                  <span class="price-note">{{ $t('sessionCounterLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('sessionCounterLanding.pricing.connection.description') }}</p>
              </div>
            </div>

            <!-- Monthly Service -->
            <div class="pricing-card featured">
              <div class="pricing-card-header">
                <h3>{{ $t('sessionCounterLanding.pricing.monthly.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price">9 EUR</span>
                  <span class="price-period">/ {{ $t('global.month') }}</span>
                  <span class="price-note">{{ $t('sessionCounterLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('sessionCounterLanding.pricing.monthly.description') }}</p>
              </div>
            </div>

            <!-- Cascade API Rate -->
            <div class="pricing-card">
              <div class="pricing-card-header">
                <h3>{{ $t('sessionCounterLanding.pricing.cascadeRate.title') }}</h3>
                <div class="pricing-amount">
                  <span class="price price-small">0.00076 EUR</span>
                  <span class="price-period">/ msg</span>
                  <span class="price-note">{{ $t('sessionCounterLanding.pricing.vatNote') }}</span>
                </div>
              </div>
              <div class="pricing-card-body">
                <p>{{ $t('sessionCounterLanding.pricing.cascadeRate.description') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('sessionCounterLanding.howItWorks.title') }}</div>
        </div>
        <div class="section-content">
          <div class="process-steps">
            <div v-for="(step, index) in 4" :key="index" class="process-step">
              <div class="step-number">{{ index + 1 }}</div>
              <div class="step-content">
                <h3 class="step-title">{{ $t(`sessionCounterLanding.howItWorks.steps[${index}].title`) }}</h3>
                <p class="step-description">{{ $t(`sessionCounterLanding.howItWorks.steps[${index}].description`) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Benefits Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title">{{ $t('sessionCounterLanding.benefits.title') }}</div>
        </div>
        <div class="section-content">
          <div class="benefits-grid">
            <div v-for="(item, index) in 4" :key="index" class="benefit-card">
              <div class="benefit-icon">
                <span :class="['pi text-2xl', index === 0 ? 'pi-globe' : index === 1 ? 'pi-chart-line' : index === 2 ? 'pi-wallet' : 'pi-cog']"></span>
              </div>
              <div class="benefit-content">
                <h3>{{ $t(`sessionCounterLanding.benefits.items[${index}].title`) }}</h3>
                <p>{{ $t(`sessionCounterLanding.benefits.items[${index}].description`) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section">
      <div class="container">
        <div class="cta-section">
          <h2>{{ $t('sessionCounterLanding.hero.title') }}</h2>
          <p class="cta-description">{{ $t('sessionCounterLanding.hero.subtitle') }}</p>
          <div class="cta-buttons">
            <NuxtLink :to="REGISTER_URL" external target="_blank" class="button filled whitespace-nowrap">
              <p>{{ $t('sessionCounterLanding.hero.cta') }}</p>
            </NuxtLink>
            <NuxtLink :to="DOCS_URL" external target="_blank" class="button outlined">
              {{ $t('sessionCounterLanding.hero.viewDocs') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="page-section">
      <div class="container">
        <ClientOnly>
          <PopularQuestions :title="$t('sessionCounterLanding.faq.title')" :questions="popularQuestions" />
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.banner-image-container {
  width: 100%;
  margin-top: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 600px;
}

@media (max-width: 900px) {
  .banner-image-container {
    margin-top: 50px;
  }
}

@media (max-width: 850px) {
  .main-banner-content {
    display: block;
  }
}

.main-banner .banner-text {
  margin-left: 0;
  align-self: center;
  max-width: 700px;
}

.main-banner-content {
  padding: 0;
}

@media (min-width: 768px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1170px) {
  .main-banner .banner-text {
    max-width: 500px;
  }
}

@media (min-width: 1300px) {
  .main-banner .banner-text {
    margin-left: calc((100% - 1300px) / 2);
    max-width: 700px;
  }
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.feature-card {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.feature-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.feature-icon.injection-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.feature-icon.counter-icon {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.feature-icon.cascade-icon {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.feature-card h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.feature-card p {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Pricing Cards */
.pricing-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.pricing-cards.three-cols {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.pricing-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 2px solid transparent;
  transition: transform 0.2s, box-shadow 0.2s;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.pricing-card.featured {
  border-color: #7c3aed;
  background: linear-gradient(180deg, #faf5ff 0%, white 100%);
}

.pricing-card-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.pricing-card-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
}

.pricing-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.price {
  font-size: 2.5rem;
  font-weight: 700;
  color: #7c3aed;
}

.price.price-small {
  font-size: 1.75rem;
}

.price-period {
  font-size: 1rem;
  color: #4b5563;
  font-weight: 400;
}

.price-note {
  font-size: 0.75rem;
  color: #6b7280;
}

.pricing-card-body {
  text-align: center;
  color: #6b7280;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Process Steps */
.process-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.process-step {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.process-step:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.step-number {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.step-description {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Benefits Grid */
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.benefit-card {
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
}

.benefit-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.benefit-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.benefit-content h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.benefit-content p {
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
}

/* CTA Section */
.cta-section {
  text-align: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  border-radius: 24px;
  color: white;
}

.cta-section h2 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.cta-description {
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-buttons .button.filled {
  background: white;
  color: #7c3aed;
}

.cta-buttons .button.outlined {
  border-color: white;
  color: white;
  background: transparent;
}
</style>
